import { useEffect } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { PREDICT_COST_FAILED_EVENT } from "../../../utils/costPrediction";

/** The gaps surfaces that rank or annotate people with a batch cost prediction. */
export enum GapsPredictCostSource {
	AddStaffSheet = "gaps_add_staff_sheet",
	Approve = "gaps_approve",
	OpenShifts = "gaps_open_shifts",
}

type BatchOutcome = {
	isError: boolean;
	error: Error | null;
};

type Context = {
	source: GapsPredictCostSource;
	departmentId: string;
	/** How many people the failed batch was asked about. */
	nurseCount: number;
};

/**
 * Failures already reported. The query cache hands every reader of a failed batch the same error object, so a sheet
 * reopened onto a failure the cache still holds finds it here; a request that fails again produces a new object and is
 * reported. Module-level because a component's own memory ends when the sheet closes. Weak, so it never keeps a
 * failure alive after the cache drops it.
 */
const reportedFailures = new WeakSet<Error>();

/** Stands in for a failure with no error attached, so even that one is reported once rather than on every change. */
const UNKNOWN_FAILURE = new Error("unknown");

/**
 * Report a failed batch cost prediction ONCE per failed request, as the classic add page reports its single prediction.
 *
 * Each surface already says the cost is unknown on screen; this is the half that makes an outage visible to us.
 * Keyed on the failure itself, not on renders or on the component: a failure that stays on screen across re-renders
 * (or a `trackError` whose identity turns over), or that a reopened sheet reads back from the cache, is one event, and
 * the next failed request is another.
 */
export const useReportPredictCostFailure = ({ isError, error }: BatchOutcome, { source, departmentId, nurseCount }: Context): void => {
	const { trackError } = useAnalytics();

	useEffect(() => {
		if (!isError) return;
		// DEFAULT: TanStack sets `error` whenever `isError` is true; the type alone still allows null.
		const failure = error ?? UNKNOWN_FAILURE;
		if (reportedFailures.has(failure)) return;
		reportedFailures.add(failure);
		trackError(PREDICT_COST_FAILED_EVENT, {
			source,
			department_id: departmentId,
			nurse_count: nurseCount,
			error_message: failure.message,
		});
	}, [isError, error, trackError, source, departmentId, nurseCount]);
};
